import { ProposalLetterhead, ProposalDocumentFooter } from "./ProposalBranding";
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, FileText, ClipboardList, DollarSign, Users, Calendar, Target, CheckCircle, Clock } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Pencil, Check, X } from "lucide-react";

export interface EventAIResults {
  companyIntroduction: string;
  eventOverview: string;
  crewPlan: string;
  shiftSchedule: string;
  laborCostBreakdown: string;
  overtimePolicy: string;
  totalEventCost: string;
  logisticsAndRequirements: string;
  termsAndConditions: string;
}

interface EventProposalResultsProps {
  results: EventAIResults | null;
  isLoading: boolean;
  onResultsChange?: (results: EventAIResults) => void;
}

const sections = [
  { key: "companyIntroduction" as const, title: "A. Company Introduction", icon: FileText },
  { key: "eventOverview" as const, title: "B. Event Overview", icon: Calendar },
  { key: "crewPlan" as const, title: "C. Crew Plan & Roles", icon: Users },
  { key: "shiftSchedule" as const, title: "D. Shift Schedule", icon: Clock },
  { key: "laborCostBreakdown" as const, title: "E. Labor Cost Breakdown", icon: DollarSign },
  { key: "overtimePolicy" as const, title: "F. Overtime Policy", icon: ClipboardList },
  { key: "totalEventCost" as const, title: "G. Total Event Cost", icon: Target },
  { key: "logisticsAndRequirements" as const, title: "H. Logistics & Requirements", icon: ClipboardList },
  { key: "termsAndConditions" as const, title: "I. Terms & Conditions", icon: CheckCircle },
];

const EventProposalResults = ({ results, isLoading, onResultsChange }: EventProposalResultsProps) => {
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Loader2 className="h-10 w-10 animate-spin text-primary mb-4" />
        <p className="text-lg font-medium">Generating Staffing Proposal...</p>
        <p className="text-sm mt-1">Building crew plan, shift schedule, and cost breakdown</p>
      </div>
    );
  }

  if (!results) return null;

  const startEdit = (key: string, value: string) => { setEditingKey(key); setEditValue(value || ""); };
  const saveEdit = () => {
    if (editingKey && results && onResultsChange) onResultsChange({ ...results, [editingKey]: editValue });
    setEditingKey(null);
  };
  const cancelEdit = () => { setEditingKey(null); setEditValue(""); };

  return (
    <div id="event-proposal-content" className="proposal-document animate-fade-in">
      <ProposalLetterhead title="Event Staffing Proposal" />

      {sections.map(({ key, title, icon: Icon }) => {
        const content = results[key];
        if (!content) return null;
        return (
          <Card key={key} data-pdf-block className="proposal-section">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-start justify-between gap-2 text-foreground text-sm">
                <span className="flex items-start gap-2 min-w-0">
                  <Icon className="h-5 w-5 text-warning" />
                  {title}
                </span>
                {onResultsChange && editingKey !== key && (
                  <Button data-html2canvas-ignore title={`Edit ${title}`} variant="ghost" size="sm" onClick={() => startEdit(key, content)} className="text-muted-foreground hover:text-foreground">
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editingKey === key ? (
                <div className="space-y-3">
                  <Textarea value={editValue} onChange={(e) => setEditValue(e.target.value)} className="min-h-[120px] bg-secondary border-border text-foreground" />
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} className="gradient-primary text-primary-foreground"><Check className="h-4 w-4 mr-1" /> Save</Button>
                    <Button size="sm" variant="outline" onClick={cancelEdit}><X className="h-4 w-4 mr-1" /> Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="prose prose-invert prose-sm max-w-none text-secondary-foreground whitespace-pre-wrap">
                  {content}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
      <ProposalDocumentFooter />
    </div>
  );
};

export default EventProposalResults;
